import { useEffect } from 'react'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'

interface UseAnnotationIterationShortcutsParams {
  isActive: boolean
  selectNeighborInVisibleList: (delta: 1 | -1) => void
  getCurrentSelectedBox: () => AnnotationDataBoundingBox | undefined
  onDeleteSelected: () => void
  onZoomToBox?: (box: AnnotationDataBoundingBox) => void
}

/**
 * Adds keyboard iteration for annotations review.
 * - ArrowDown/ArrowUp: move selection within the current visible list
 * - Delete: delete currently selected box
 */
export function useAnnotationIterationShortcuts({
  isActive,
  selectNeighborInVisibleList,
  getCurrentSelectedBox,
  onDeleteSelected,
  onZoomToBox,
}: UseAnnotationIterationShortcutsParams): void {
  useEffect(() => {
    if (!isActive) return
    const handler = (e: KeyboardEvent) => {
      // Ignore when the user is typing in inputs/textareas/selects/contenteditable
      const target = e.target as HTMLElement | null
      if (target) {
        const tagName = target.tagName
        const isEditableEl =
          target.isContentEditable ||
          tagName === 'INPUT' ||
          tagName === 'TEXTAREA' ||
          tagName === 'SELECT'
        if (isEditableEl) return
      }
      const key = e.key
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Delete') {
        e.preventDefault()
      }
      switch (key) {
        case 'ArrowDown': {
          selectNeighborInVisibleList(1)
          // Allow state to update before resolving current selection and zooming
          setTimeout(() => {
            const box = getCurrentSelectedBox()
            if (box && onZoomToBox) onZoomToBox(box)
          }, 0)
          break
        }
        case 'ArrowUp': {
          selectNeighborInVisibleList(-1)
          setTimeout(() => {
            const box = getCurrentSelectedBox()
            if (box && onZoomToBox) onZoomToBox(box)
          }, 0)
          break
        }
        case 'Delete': {
          onDeleteSelected()
          break
        }
        default:
          break
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isActive, selectNeighborInVisibleList, getCurrentSelectedBox, onDeleteSelected, onZoomToBox])
}

export default useAnnotationIterationShortcuts
